import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaTitle } from "@/types/media";
import { formatHeat } from "@/lib/media";

interface HeroCarouselProps {
  items: MediaTitle[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const count = items.length;

  const next = useCallback(() => setIndex((v) => (v + 1) % count), [count]);
  const prev = useCallback(() => setIndex((v) => (v - 1 + count) % count), [count]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  if (count === 0) return null;
  const active = items[index];

  return (
    <section className="relative w-full h-[440px] md:h-[600px] overflow-hidden bg-primary-900">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 影视详情`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/80 via-primary-950/20 to-transparent" />

      <div className="relative h-full w-full px-4 md:px-8 flex items-center">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-accent-500 text-background-50">
              {active.type === "movie" ? "电影推荐" : "剧集推荐"}
            </span>
            <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-background-50/15 text-background-50 backdrop-blur-sm border border-background-50/20">
              {active.region} · {active.year}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 font-heading leading-tight">
            {active.title}
          </h1>
          <p className="mt-1 text-sm md:text-base text-accent-300 font-medium tracking-wide">
            {active.originalTitle}
          </p>
          <p className="mt-4 text-sm md:text-base text-background-200 leading-relaxed line-clamp-3 max-w-xl">
            {active.synopsis}
          </p>

          <div className="mt-5 flex items-center gap-4 text-sm text-background-200">
            <span className="inline-flex items-center gap-1 font-semibold text-accent-400">
              <i className="ri-star-fill" />
              {active.rating.toFixed(1)}
            </span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <i className="ri-fire-fill text-accent-400" />
              {formatHeat(active.heat)} 热度
            </span>
            <span className="hidden sm:inline-flex items-center gap-1">
              <i className="ri-clapperboard-line" />
              {active.director}
            </span>
          </div>

          <div className="mt-7 flex items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg" />
              查看详情
            </Link>
            <Link
              to={active.type === "movie" ? "/category/movie" : "/category/tv"}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-background-50/15 text-background-50 text-sm font-semibold border border-background-50/25 hover:bg-background-50/25 transition-colors backdrop-blur-sm whitespace-nowrap"
            >
              <i className="ri-apps-2-line text-lg" />
              更多同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={prev}
        aria-label="上一张"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/25 hover:bg-background-50/30 transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={next}
        aria-label="下一张"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-background-50/15 text-background-50 border border-background-50/25 hover:bg-background-50/30 transition-colors backdrop-blur-sm cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? "w-8 bg-accent-500" : "w-2.5 bg-background-50/50 hover:bg-background-50/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}